import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import { format } from '@formkit/tempo';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import { Link } from '@/components/Elements/Link';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { buildAnalyzeSearchUrl } from '@/features/analyze/lib/buildAnalyzeSearchUrl';
import { buildOrFilterCondition } from '@/features/analyze/lib/buildOrFilterCondition';

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';

  try {
    return format(new Date(value), 'YYYY-MM-DD');
  } catch {
    return '-';
  }
};

const organizationTableRowFragment = graphql(`
  fragment OrganizationTableRowFragment on Organization {
    id
    name
    originalName
    abbreviation
    status
    type
    country
    countryTags
    latestArticleDate
    oldestArticleDate
  }
`);

const SearchIconLink = ({ to, label }: { to: string; label: string }) => (
  <Link
    to={to}
    underline="none"
    color="secondary"
    className={clsx(
      'inline-flex items-center justify-center self-start rounded border border-gray-200 bg-white p-1 align-top leading-none text-gray-500 transition hover:border-gray-300 hover:text-gray-700',
      'opacity-0 group-hover:opacity-100'
    )}
    target="_blank"
    rel="noopener"
    title={label}
    aria-label={label}
  >
    <MagnifyingGlassIcon className="block size-[0.9em]" aria-hidden />
  </Link>
);

const SearchableText = ({
  text,
  to,
  className,
}: {
  text: string;
  to: string;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <span className={clsx('group inline-flex items-start gap-1', className)}>
      <span className="min-w-0 break-words underline decoration-dotted underline-offset-1">
        {text}
      </span>
      <SearchIconLink to={to} label={t('記事検索')} />
    </span>
  );
};

const getRawValueIfValid = (value: string | null | undefined) =>
  value?.trim() ? value : undefined;

type OrganizationTableRowProps = {
  data: FragmentOf<typeof organizationTableRowFragment>;
};

export const OrganizationTableRow = ({ data }: OrganizationTableRowProps) => {
  const organization = readFragment(organizationTableRowFragment, data);
  const isJapaneseLocale = getCurrentLanguage() === 'ja';

  const {
    name: localizedNameRaw,
    originalName: originalNameRaw,
    abbreviation,
    country: countryRaw,
    type: organizationTypeRaw,
    countryTags: countryTagsRaw,
    status,
    latestArticleDate,
    oldestArticleDate,
  } = organization;

  const localizedName = localizedNameRaw?.trim();
  const originalName = originalNameRaw?.trim();
  const displayAbbreviation = abbreviation?.trim();
  const country = countryRaw?.trim();
  const organizationType = organizationTypeRaw?.trim();

  const primaryName = originalName || localizedName || '-';
  const secondaryName =
    isJapaneseLocale &&
    localizedName &&
    originalName &&
    localizedName !== originalName
      ? localizedName
      : undefined;

  const filterTargetName = getRawValueIfValid(originalNameRaw);
  const countryFilterValue = getRawValueIfValid(countryRaw);
  const organizationTypeFilterValue = getRawValueIfValid(organizationTypeRaw);

  return (
    <tr>
      <td className="p-4 align-top">
        <div className="flex flex-col gap-1 text-sm">
          {filterTargetName ? (
            <SearchableText
              className="font-semibold text-gray-900"
              text={`${primaryName}${
                displayAbbreviation ? `（${displayAbbreviation}）` : ''
              }`}
              to={buildAnalyzeSearchUrl(
                buildOrFilterCondition({
                  organizations: filterTargetName,
                })
              )}
            />
          ) : (
            <span className="font-semibold text-gray-900">
              {primaryName}
              {displayAbbreviation ? `（${displayAbbreviation}）` : ''}
            </span>
          )}
          {secondaryName && (
            <span className="text-xs text-gray-600">{secondaryName}</span>
          )}
        </div>
      </td>
      <td className="p-4 align-top text-sm text-gray-700">{status || '-'}</td>
      <td className="p-4 align-top text-sm text-gray-700">
        {organizationType && organizationTypeFilterValue ? (
          <SearchableText
            text={organizationType}
            to={buildAnalyzeSearchUrl(
              buildOrFilterCondition({
                organizationTypes: organizationTypeFilterValue,
              })
            )}
          />
        ) : (
          '-'
        )}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {country && countryFilterValue ? (
          <SearchableText
            text={country}
            to={buildAnalyzeSearchUrl(
              buildOrFilterCondition({
                countries: countryFilterValue,
              })
            )}
          />
        ) : (
          '-'
        )}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {countryTagsRaw ? (
          <div className="flex flex-col gap-1">
            {countryTagsRaw.map((tag) => {
              const displayTag = tag.trim();

              return (
                <SearchableText
                  key={`${tag}-${displayTag}`}
                  text={displayTag}
                  to={buildAnalyzeSearchUrl(
                    buildOrFilterCondition({
                      countryTags: displayTag,
                    })
                  )}
                />
              );
            })}
          </div>
        ) : (
          '-'
        )}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {formatDate(latestArticleDate)}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {formatDate(oldestArticleDate)}
      </td>
    </tr>
  );
};

OrganizationTableRow.fragments = {
  organization: organizationTableRowFragment,
};
